import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, Check, Download, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { config } from "../config/siteConfig";
import { LEAD_FORM_EVENT } from "../lib/leadForm";

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: "", email: "", phone: "" };

const Field = ({ id, label, type = "text", value, onChange, placeholder, autoComplete }) => (
  <label htmlFor={id} className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">{label}</span>
    <input
      id={id}
      name={id}
      type={type}
      required
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoComplete={autoComplete}
      data-testid={`lead-form-${id}-input`}
      className="mt-2 w-full bg-transparent border-b border-white/25 focus:border-[#D4AF37] outline-none py-3 text-base text-white placeholder:text-white/25 transition-colors duration-300"
    />
  </label>
);

const LeadForm = ({ source, onSuccess }) => {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/api/leads`, { ...form, source });
      onSuccess(form.name);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(Array.isArray(detail) ? "Please check your details and try again." : detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="lead-form" className="mt-8 space-y-6">
      <Field id="name" label="Full name" value={form.name} onChange={update("name")} placeholder="Your name" autoComplete="name" />
      <Field id="email" label="Email" type="email" value={form.email} onChange={update("email")} placeholder="you@example.com" autoComplete="email" />
      <Field id="phone" label="Phone" type="tel" value={form.phone} onChange={update("phone")} placeholder="+1 (555) 000-0000" autoComplete="tel" />
      {error && (
        <p data-testid="lead-form-error" className="text-sm text-[#F0A3A3]">{error}</p>
      )}
      <button
        type="submit"
        disabled={loading}
        data-testid="lead-form-submit-button"
        className="group w-full inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#08162B] font-mono uppercase tracking-[0.18em] text-xs sm:text-sm px-8 py-4 hover:bg-white transition-colors duration-300 disabled:opacity-60"
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : null}
        <span>{loading ? "Sending" : "Send Me The Book"}</span>
        {!loading && <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
      </button>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/35 text-center">
        {config.book.note}
      </p>
    </form>
  );
};

const Success = ({ name }) => (
  <div data-testid="lead-success" className="mt-6">
    <div className="flex items-center gap-3 text-[#D4AF37]">
      <span className="h-9 w-9 rounded-full border border-[#D4AF37] flex items-center justify-center">
        <Check size={16} />
      </span>
      <span className="font-mono text-[10px] uppercase tracking-[0.3em]">You're in</span>
    </div>
    <p className="mt-6 font-display text-3xl sm:text-4xl tracking-[0.02em] leading-[1.05] text-white">
      CONGRATULATIONS{name ? `, ${name.split(" ")[0].toUpperCase()}` : ""}.
    </p>
    <p className="mt-4 text-base text-white/70 leading-relaxed">
      Your copy of {config.book.title} is ready. Open it below and start with the first principle today.
    </p>
    <a
      href={config.book.pdfUrl}
      target="_blank"
      rel="noreferrer"
      download
      data-testid="lead-success-download-button"
      className="group mt-8 w-full inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#08162B] font-mono uppercase tracking-[0.18em] text-xs sm:text-sm px-8 py-4 hover:bg-white transition-colors duration-300"
    >
      <Download size={16} />
      <span>Open The Ecom Bible (PDF)</span>
    </a>
  </div>
);

export const LeadModal = () => {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState("website");
  const [doneName, setDoneName] = useState(null);

  useEffect(() => {
    const onOpen = (e) => {
      setSource(e.detail?.source || "website");
      setDoneName(null);
      setOpen(true);
    };
    window.addEventListener(LEAD_FORM_EVENT, onOpen);
    return () => window.removeEventListener(LEAD_FORM_EVENT, onOpen);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="lead-modal"
        className="bg-[#08162B] border border-white/10 text-white p-8 sm:p-10 max-w-md rounded-none [&>button]:text-white/60"
      >
        <DialogTitle className="font-display text-3xl sm:text-4xl tracking-[0.02em] leading-[1.05] text-white">
          {doneName === null ? "GET THE ECOM BIBLE" : "YOUR COPY IS READY"}
        </DialogTitle>
        <DialogDescription className="text-white/60 text-base">
          {doneName === null
            ? "Tell us where to send it. Instant access the moment you submit."
            : "Save it, share it, and put it to work."}
        </DialogDescription>
        {doneName === null ? (
          <LeadForm source={source} onSuccess={setDoneName} />
        ) : (
          <Success name={doneName} />
        )}
      </DialogContent>
    </Dialog>
  );
};
